/** Section 5 is a view of Stage 2, never a second variant state store. */
class BanzrcGtPowerGuide extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.addEventListener('click', this.onClick, { signal: this.abort.signal });
    document.addEventListener('shopify:section:load', this.bindConfigurator, { signal: this.abort.signal });
    customElements.whenDefined('banzrc-gt-configurator').then(() => {
      if (this.isConnected) this.bindConfigurator();
    });
  }

  bindConfigurator = () => {
    const configurator = [...document.querySelectorAll('banzrc-gt-configurator')].find(
      (element) => element.dataset.productId === this.dataset.productId
    );
    if (configurator !== this.configurator) {
      this.observer?.disconnect();
      this.configurator = configurator;
      if (configurator) {
        this.observer = new MutationObserver(() => this.render());
        this.observer.observe(configurator, {
          attributes: true,
          subtree: true,
          attributeFilter: ['aria-pressed', 'data-selection-complete', 'data-initialized', 'data-price', 'data-inventory', 'data-available', 'data-motor-6s', 'data-motor-8s', 'data-esc-6s', 'data-esc-8s', 'data-servo-6s', 'data-servo-8s'],
        });
      }
    }
    this.render();
  };

  /** Computes a view directly from the existing configurator at read time. */
  systemView(power) {
    const config = this.configurator;
    const rows = config?.rows || [];
    const color = config?.selectedColor;
    const matching = rows.filter(row => row.power === power && (!color || row.color === color));
    const variant = color ? matching[0] : null;
    const available = matching.some(row => row.available);
    const prices = [...new Set(matching.map(row => row.price))];
    let stock = this.dataset.chooseColor;
    let reason = color ? '' : this.dataset.chooseColor;
    if (!config || !rows.length) {
      stock = reason = this.dataset.noConnection;
    } else if (!matching.length) {
      stock = reason = this.dataset.unavailable;
    } else if (!available) {
      stock = reason = color ? this.dataset.soldOut : 'Out of stock';
    } else if (variant) {
      stock = variant.inventory > 0
        ? `${this.dataset.inStock} · ${variant.inventory}`
        : this.dataset.orderable;
    }
    return {
      price: !matching.length ? '—' : variant?.price || (prices.length === 1 ? prices[0] : this.dataset.chooseColor),
      stock,
      reason,
      available,
      selected: config?.selectedPower === power,
    };
  }

  render() {
    const setText = (element, value) => {
      if (element && element.textContent !== value) element.textContent = value;
    };
    setText(this.querySelector('[data-guide-color]'), this.configurator?.selectedColor || this.dataset.chooseColor);
    setText(this.querySelector('[data-guide-announcement]'), this.configurator?.querySelector('[data-selection-message]')?.textContent || '');
    this.querySelectorAll('[data-guide-system]').forEach(system => {
      const power = system.dataset.guideSystem;
      const view = this.systemView(power);
      setText(system.querySelector('[data-guide-price]'), view.price);
      setText(system.querySelector('[data-guide-stock]'), view.stock);
      setText(system.querySelector('[data-guide-reason]'), view.reason);
      const button = system.querySelector('[data-guide-choose]');
      const original = this.configurator?.querySelector(`button[data-power="${power}"]`);
      button.disabled = !view.available || !original || original.disabled;
      button.setAttribute('aria-pressed', String(view.selected));
      system.querySelector('[data-guide-selected]').hidden = !view.selected;
      system.querySelectorAll('[data-guide-spec]').forEach(element => {
        const text = this.configurator?.getAttribute(`data-${element.dataset.guideSpec}-${power.toLowerCase()}`);
        setText(element, text || this.dataset.specFallback);
      });
    });
  }

  onClick = (event) => {
    const choose = event.target.closest('[data-guide-choose]');
    if (choose && this.contains(choose)) {
      const power = choose.dataset.guideChoose;
      // Recheck at click-time; never substitute another color or variant.
      const original = this.configurator?.querySelector(`button[data-power="${power}"]`);
      if (choose.disabled || !this.systemView(power).available || !original || original.disabled) return;
      original.click();
      this.render();
      const anchor = document.getElementById('banzrc-gt-configuration');
      anchor?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      // Focus the existing control without moving the viewport a second time.
      const focusTarget = this.configurator.selectedColor ? original : this.configurator.querySelector('button[data-color]:not(:disabled)');
      focusTarget?.focus({ preventScroll: true });
    }
    const chatLink = event.target.closest('[data-guide-chat]');
    if (chatLink && this.contains(chatLink)) {
      const launcher = document.querySelector('shopify-chat')?.shadowRoot?.querySelector('button[part="activator"]');
      if (launcher) {
        event.preventDefault();
        launcher.click();
      }
    }
  };

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.configurator = null;
  }
}

if (!customElements.get('banzrc-gt-power-guide')) {
  customElements.define('banzrc-gt-power-guide', BanzrcGtPowerGuide);
}
